import React, { useLayoutEffect } from 'react';
import { Users, Globe, Package, Smartphone, Eye, ShieldCheck, Zap, Star, Headphones, BadgePercent, PackageCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Header from 'pages/HomePage/components/Header';
import Footer from 'pages/HomePage/components/Footer';
import './GioiThieuPage.scss';

const COMPANY_IMG = 'https://pub-99c08cb1333848799ea7396fa0c922e1.r2.dev/sso/LOGO%20PLE%20(39).jpg';

const FEATURE_ICONS = [Users, Globe, Package, Smartphone];
const ADVANTAGE_ICONS = [Headphones, BadgePercent, PackageCheck];
const ADVANTAGE_COLORS = ['#00ADEF', '#10b981', '#f97316'];
const FEATURE_COLORS = ['#00ADEF', '#0ea5e9', '#10b981', '#f97316'];

const GioiThieuPage: React.FC = () => {
  const { t } = useTranslation();

  const features = (t('gioiThieu.features', { returnObjects: true }) as { title: string; desc: string }[]);
  const advantages = (t('gioiThieu.advantages', { returnObjects: true }) as { title: string; desc: string }[]);

  useLayoutEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            (e.target as HTMLElement).setAttribute('data-visible', 'true');
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0, rootMargin: '200px 0px 200px 0px' }
    );
    document.documentElement.classList.add('js-animations-ready');
    document.querySelectorAll('[data-reveal],[data-stagger]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Header />

      {/* SECTION 1 — Company intro + feature grid */}
      <section className="gt-intro" aria-labelledby="gt-intro-heading">
        <div className="gt-intro__container">

          <div className="gt-intro__img-frame" data-reveal data-anim="left">
            <span className="gt-intro__corner gt-intro__corner--tl" aria-hidden="true" />
            <span className="gt-intro__corner gt-intro__corner--br" aria-hidden="true" />
            <div className="gt-intro__img-col">
              <img
                src={COMPANY_IMG}
                alt={t('gioiThieu.intro.imgAlt')}
                className="gt-intro__img"
                loading="lazy"
              />
            </div>
          </div>

          <div className="gt-intro__right" data-reveal data-anim="right">
            <span className="gt-intro__eyebrow">{t('gioiThieu.intro.eyebrow')}</span>
            <h2 className="gt-intro__heading" id="gt-intro-heading">
              {t('gioiThieu.intro.headingPrefix')} <span className="gt-intro__brand">Giao Hàng Thông Minh</span>
            </h2>
            <div className="gt-intro__rule" />
            <p className="gt-intro__body">{t('gioiThieu.intro.body1')}</p>
            <p className="gt-intro__body">{t('gioiThieu.intro.body2')}</p>

            <ul className="gt-intro__strips" data-stagger>
              {features.map(({ title, desc }, i) => (
                <li key={i} className="gt-strip">
                  <div
                    className="gt-strip__icon-wrap"
                    style={{ background: FEATURE_COLORS[i], boxShadow: `0 8px 24px ${FEATURE_COLORS[i]}55` }}
                  >
                    {React.createElement(FEATURE_ICONS[i], { size: 22, strokeWidth: 1.75, color: '#fff' })}
                  </div>
                  <div className="gt-strip__content">
                    <p className="gt-strip__title">{title}</p>
                    <p className="gt-strip__desc">{desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

        </div>
      </section>

      {/* SECTION 2 — Vision + Core values */}
      <section className="gt-vision" aria-labelledby="gt-vision-heading">

        <div className="gt-vision__header" data-reveal data-anim="up">
          <span className="gt-vision__eyebrow">{t('gioiThieu.vision.eyebrow')}</span>
          <h2 className="gt-vision__title" id="gt-vision-heading">
            {t('gioiThieu.vision.title')} &amp; <span className="gt-vision__accent">{t('gioiThieu.vision.titleAccent')}</span>
          </h2>
        </div>

        <div className="gt-vision__container">

          <div className="gt-vision__card" data-reveal data-anim="left">
            <div className="gt-vision__card-icon">
              <Eye size={32} strokeWidth={1.5} />
            </div>
            <div className="gt-vision__card-label">{t('gioiThieu.vision.visionLabel')}</div>
            <h3 className="gt-vision__card-heading">{t('gioiThieu.vision.visionHeading')}</h3>
            <p className="gt-vision__card-body">{t('gioiThieu.vision.visionBody')}</p>
            <div className="gt-vision__card-rule" />
          </div>

          <div className="gt-vision__card" data-reveal data-anim="right">
            <div className="gt-vision__card-icon gt-vision__card-icon--alt">
              <ShieldCheck size={32} strokeWidth={1.5} />
            </div>
            <div className="gt-vision__card-label">{t('gioiThieu.vision.valuesLabel')}</div>
            <h3 className="gt-vision__card-heading">{t('gioiThieu.vision.valuesHeading')}</h3>
            <p className="gt-vision__card-body">{t('gioiThieu.vision.valuesBody')}</p>
            <div className="gt-vision__card-rule" />
            <div className="gt-vision__pills">
              <span className="gt-vision__pill gt-vision__pill--amber"><Zap size={13} /> {t('gioiThieu.vision.pillSpeed')}</span>
              <span className="gt-vision__pill gt-vision__pill--blue"><Star size={13} /> {t('gioiThieu.vision.pillTrust')}</span>
              <span className="gt-vision__pill gt-vision__pill--green"><ShieldCheck size={13} /> {t('gioiThieu.vision.pillTech')}</span>
            </div>
          </div>

        </div>
      </section>

      {/* SECTION 3 — Advantages */}
      <section className="gt-adv" aria-labelledby="gt-adv-heading">
        <div className="gt-adv__split">

          <div className="gt-adv__left" data-reveal data-anim="left">
            <span className="gt-eyebrow gt-eyebrow--dark">{t('gioiThieu.adv.eyebrow')}</span>
            <h2 className="gt-adv__heading" id="gt-adv-heading">
              {t('gioiThieu.adv.heading')} <span className="gt-adv__heading-accent">{t('gioiThieu.adv.headingAccent')}</span>
            </h2>
            <p className="gt-adv__sub">{t('gioiThieu.adv.sub')}</p>

            <div className="gt-adv__stats">
              <div className="gt-adv__stat">
                <span className="gt-adv__stat-num">7</span>
                <span className="gt-adv__stat-label">{t('gioiThieu.adv.statSupport')}</span>
              </div>
              <div className="gt-adv__stat-div" />
              <div className="gt-adv__stat">
                <span className="gt-adv__stat-num">0đ</span>
                <span className="gt-adv__stat-label">{t('gioiThieu.adv.statCod')}</span>
              </div>
              <div className="gt-adv__stat-div" />
              <div className="gt-adv__stat">
                <span className="gt-adv__stat-num">3x</span>
                <span className="gt-adv__stat-label">{t('gioiThieu.adv.statRedeliver')}</span>
              </div>
            </div>

            <blockquote className="gt-adv__quote">{t('gioiThieu.adv.quote')}</blockquote>
          </div>

          <div className="gt-adv__mindmap" data-reveal data-anim="right">
            <svg className="gt-adv__mm-svg" aria-hidden="true" viewBox="0 0 440 320" preserveAspectRatio="none">
              <path d="M -80,160 C -20,160 -20,44 0,44" fill="none" stroke="rgba(0,173,239,0.5)" strokeWidth="2" strokeDasharray="7,5" />
              <path d="M -80,160 C -40,160 -40,160 0,160" fill="none" stroke="rgba(16,185,129,0.5)" strokeWidth="2" strokeDasharray="7,5" />
              <path d="M -80,160 C -20,160 -20,276 0,276" fill="none" stroke="rgba(249,115,22,0.5)" strokeWidth="2" strokeDasharray="7,5" />
              <circle cx="-80" cy="160" r="5" fill="rgba(100,116,139,0.5)" />
              <circle cx="-80" cy="160" r="10" fill="rgba(100,116,139,0.1)" />
              <circle cx="0" cy="44" r="3.5" fill="rgba(0,173,239,0.55)" />
              <circle cx="0" cy="160" r="3.5" fill="rgba(16,185,129,0.55)" />
              <circle cx="0" cy="276" r="3.5" fill="rgba(249,115,22,0.55)" />
            </svg>
            {advantages.map(({ title, desc }, i) => (
              <div key={i} className={`gt-adv__mm-node gt-adv__mm-node--${i + 1}`}
                style={{ '--node-color': ADVANTAGE_COLORS[i] } as React.CSSProperties}>
                <div className="gt-adv__mm-node-icon"
                  style={{ background: `${ADVANTAGE_COLORS[i]}15`, color: ADVANTAGE_COLORS[i], borderColor: `${ADVANTAGE_COLORS[i]}35` }}>
                  {React.createElement(ADVANTAGE_ICONS[i], { size: 22, strokeWidth: 1.75 })}
                </div>
                <div className="gt-adv__mm-node-text">
                  <p className="gt-adv__mm-node-title">{title}</p>
                  <p className="gt-adv__mm-node-desc">{desc}</p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      <Footer />
    </>
  );
};

export default GioiThieuPage;
